import { useState } from 'react';

interface Appointment {
  id: string;
  nombre: string;
  fecha: string;
  hora: string;
  servicio: string;
  estado: string;
}

interface Props {
  appointments: Appointment[];
}

const STATE_COLORS: Record<string, string> = {
  pendiente: 'bg-yellow-400',
  confirmada: 'bg-green-400',
  completada: 'bg-zinc-400',
  cancelada: 'bg-red-400',
};

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const AppointmentCalendar = ({ appointments }: Props) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7; // Monday=0
  const totalDays = lastDay.getDate();

  const cells: (number | null)[] = [
    ...Array(startDow).fill(null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const aptByDate: Record<string, Appointment[]> = {};
  for (const apt of appointments) {
    if (!aptByDate[apt.fecha]) aptByDate[apt.fecha] = [];
    aptByDate[apt.fecha].push(apt);
  }

  const fmt = (d: number) => {
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  };

  const prev = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); setSelectedDay(null); };
  const next = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); setSelectedDay(null); };

  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  const selectedApts = selectedDay ? (aptByDate[selectedDay] ?? []) : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={prev} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer">
          <i className="ri-arrow-left-s-line" />
        </button>
        <p className="text-white font-bold text-sm uppercase tracking-widest">{MONTHS[month]} {year}</p>
        <button onClick={next} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer">
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {DAYS.map(d => (
          <div key={d} className="text-center text-zinc-500 text-[10px] font-bold uppercase tracking-wider py-2">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (!day) return <div key={idx} />;
          const dateStr = fmt(day);
          const dayApts = aptByDate[dateStr] ?? [];
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDay;
          return (
            <button
              key={idx}
              onClick={() => setSelectedDay(isSelected ? null : dateStr)}
              className={`relative aspect-square flex flex-col items-center justify-start pt-1.5 cursor-pointer transition-all border ${
                isSelected ? 'bg-yellow-400/10 border-yellow-400/40' :
                isToday ? 'bg-zinc-700/50 border-zinc-600' :
                'bg-zinc-900 border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <span className={`text-xs font-bold ${isToday ? 'text-yellow-400' : 'text-zinc-300'}`}>{day}</span>
              {dayApts.length > 0 && (
                <div className="flex flex-wrap gap-0.5 mt-1 justify-center">
                  {dayApts.slice(0, 3).map((a) => (
                    <span key={a.id} className={`w-1.5 h-1.5 rounded-full ${STATE_COLORS[a.estado] ?? 'bg-zinc-400'}`} />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <div className="mt-4 border border-zinc-800 bg-zinc-900/50 p-4">
          <p className="text-yellow-400 text-xs font-bold uppercase tracking-wider mb-3">{selectedDay} — {selectedApts.length} cita(s)</p>
          {selectedApts.length === 0 ? (
            <p className="text-zinc-600 text-sm">Sin citas para este día</p>
          ) : (
            <div className="space-y-2">
              {selectedApts.map(a => (
                <div key={a.id} className="flex items-center gap-3 py-2 border-b border-zinc-800 last:border-0">
                  <span className={`w-2 h-2 rounded-full ${STATE_COLORS[a.estado] ?? 'bg-zinc-400'} flex-shrink-0`} />
                  <div>
                    <p className="text-white text-sm font-bold">{a.hora} — {a.nombre}</p>
                    <p className="text-zinc-500 text-xs">{a.servicio}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendar;
